import {
  useWSModalCustom,
  WSElement,
  WSForm,
  WSInputDate,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";

import { useGetNow } from "../../../shared/hooks/useGetNow";
import { addDays, calculateDayDifference } from "../../../utils/dates";

const DEADLINE_OPTIONS = [
  { label: "14 days", value: "14" },
  { label: "21 days", value: "21" },
  { label: "30 days", value: "30" },
  { label: "45 days", value: "45" },
  { label: "60 days", value: "60" },
  { label: "Custom date", value: "custom" }
];

type EditRenewalDeadlineResult = {
  renewalDeadlineDays: number;
  customDate?: Date;
};

type FormData = {
  deadlineOption: string;
  customDate?: string;
};

type Props = {
  renewalDeadlineDays: number;
} & WSModalCustomProps<EditRenewalDeadlineResult | undefined>;

const validationSchema = Yup.object().shape({
  customDate: Yup.string().when("deadlineOption", {
    is: "custom",
    then: Yup.string()
      .required("Date is required")
      .test("future-date", "Must be a future date", value => {
        if (!value) return false;
        const date = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date > today;
      })
  }),
  deadlineOption: Yup.string().required("Please select an option")
});

const DEADLINE_VALUES = new Set(
  DEADLINE_OPTIONS.filter(o => o.value !== "custom").map(o => o.value)
);

const getDefaultDeadlineOption = (days: number): string => {
  const value = String(days);
  return DEADLINE_VALUES.has(value) ? value : "";
};

export const ModalEditRenewalDeadline: React.FC<Props> = ({
  renewalDeadlineDays,
  ...modalProps
}) => {
  const now = useGetNow();

  const handleSubmit = (formData: FormData): void => {
    const isCustom = formData.deadlineOption === "custom";

    const result: EditRenewalDeadlineResult = {
      renewalDeadlineDays:
        isCustom && formData.customDate
          ? calculateDayDifference(new Date(formData.customDate), new Date())
          : Number(formData.deadlineOption)
    };

    modalProps.onClose(result);
  };

  return (
    <WSForm<FormData>
      defaultValues={{
        customDate: "",
        deadlineOption: getDefaultDeadlineOption(renewalDeadlineDays)
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ watch }) => {
        const deadlineOption = watch("deadlineOption");
        const isCustom = deadlineOption === "custom";

        return (
          <WSModal
            actions={{
              alignment: "fill",
              buttons: [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => modalProps.onClose(undefined),
                  type: "button"
                },
                {
                  kind: "Primary",
                  label: "Save changes"
                }
              ]
            }}
            size="S"
            title="Edit renewal deadline"
            {...modalProps}
          >
            <WSList gap="M">
              <WSElement
                colorBackground="gray50"
                p="M"
                style={{ borderRadius: 4 }}
              >
                <WSText.ParagraphSm color="gray500">
                  Current renewal deadline
                </WSText.ParagraphSm>
                <WSText mt="XS">{renewalDeadlineDays} days</WSText>
              </WSElement>

              <WSForm.Field
                component={WSSelect}
                componentProps={{
                  internalSearch: false,
                  options: DEADLINE_OPTIONS,
                  placeholder: "Select an option"
                }}
                label="Edit renewal deadline"
                name="deadlineOption"
              />

              {isCustom && (
                <WSForm.Field
                  component={WSInputDate}
                  componentProps={{
                    minDate: addDays(now, 1)
                  }}
                  label="New deadline date"
                  name="customDate"
                />
              )}
            </WSList>
          </WSModal>
        );
      }}
    </WSForm>
  );
};

export const useEditRenewalDeadlineModal = (): ReturnType<
  typeof useWSModalCustom<Props, EditRenewalDeadlineResult | undefined>
> => useWSModalCustom(ModalEditRenewalDeadline);
